/**
 * Tipos del AI Copilot y transcripción de llamadas.
 */

// ─── AI Copilot ──────────────────────────────────────────────────────────────

export type AICopilotAction =
  | 'chat'
  | 'draft_email'
  | 'summarize'
  | 'next_steps'
  | 'call_prep'
  | 'analyze_deal'
  | 'draft_wa';

export type AICopilotContextType = 'contact' | 'deal' | 'call' | 'general';

export interface AICopilotMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface AICopilotThread {
  id: string;
  tenant_id: string;
  user_id: string;
  context_type: AICopilotContextType | null;
  context_id: string | null;
  title: string | null;
  messages: AICopilotMessage[];
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AICopilotRequest {
  action: AICopilotAction;
  context_type?: AICopilotContextType;
  context_id?: string;
  prompt?: string;
  thread_id?: string;
  /** Multi-tenant V1 (§18): tenant activo del frontend. Si no se pasa, EF cae al fallback legacy. */
  tenant_id?: string;
}

export interface AICopilotResponse {
  success: boolean;
  response: string;
  thread_id: string | null;
  action: AICopilotAction;
  provider: 'openai' | 'anthropic';
  model?: string;
  error?: string;
}

/** Callback for streaming tokens */
export type AICopilotStreamCallback = (chunk: { token?: string; done?: boolean; response?: string; thread_id?: string | null; error?: string }) => void;

// ─── Quick actions para el copilot (botones pre-definidos) ──────────────────

export interface AICopilotQuickAction {
  action: AICopilotAction;
  label: string;
  icon: string;
  description: string;
}

export const AI_QUICK_ACTIONS: Record<AICopilotContextType, AICopilotQuickAction[]> = {
  contact: [
    { action: 'summarize', label: 'Resumir contacto', icon: '📋', description: 'Resumen ejecutivo del contacto' },
    { action: 'call_prep', label: 'Preparar llamada', icon: '📞', description: 'Briefing antes de llamar' },
    { action: 'draft_wa', label: 'Escribir WhatsApp', icon: '💬', description: 'Generar mensaje de WhatsApp' },
    { action: 'draft_email', label: 'Escribir email', icon: '✉️', description: 'Generar borrador de email' },
    { action: 'next_steps', label: 'Próximos pasos', icon: '🎯', description: 'Sugerir acciones a tomar' },
  ],
  deal: [
    { action: 'analyze_deal', label: 'Analizar deal', icon: '📊', description: 'Probabilidad de cierre y riesgos' },
    { action: 'next_steps', label: 'Próximos pasos', icon: '🎯', description: 'Qué hacer para cerrar el deal' },
    { action: 'summarize', label: 'Resumen del deal', icon: '📋', description: 'Estado actual y contexto' },
    { action: 'draft_wa', label: 'Mensaje WhatsApp', icon: '💬', description: 'Follow-up por WhatsApp' },
    { action: 'draft_email', label: 'Email de seguimiento', icon: '✉️', description: 'Follow-up por email' },
  ],
  call: [
    { action: 'summarize', label: 'Resumir llamada', icon: '📋', description: 'Resumen de la conversación' },
    { action: 'next_steps', label: 'Próximos pasos', icon: '🎯', description: 'Qué hacer después de la llamada' },
    { action: 'draft_wa', label: 'Follow-up WA', icon: '💬', description: 'Mensaje de seguimiento por WhatsApp' },
  ],
  general: [
    { action: 'chat', label: 'Chat libre', icon: '💡', description: 'Pregunta lo que quieras' },
  ],
};

// ─── Call Transcription ──────────────────────────────────────────────────────

export type TranscriptionStatus = 'none' | 'processing' | 'completed' | 'failed';
export type CallSentiment = 'positive' | 'neutral' | 'negative';

export interface CallTranscriptionData {
  transcription: string | null;
  transcription_summary: string | null;
  sentiment: CallSentiment | null;
  key_topics: string[] | null;
  action_items: Array<{ task: string; assignee_suggestion: string }> | null;
  transcription_status: TranscriptionStatus;
}

// ─── AI Config (tenant settings) ─────────────────────────────────────────────

export interface AIConfig {
  id: string;
  tenant_id: string;
  provider: 'openai' | 'anthropic';
  model: string;
  copilot_enabled: boolean;
  email_writer_enabled: boolean;
  call_transcription_enabled: boolean;
  lead_scoring_enabled: boolean;
  deal_insights_enabled: boolean;
  custom_instructions: string | null;
  max_tokens: number;
  temperature: number;
}
